import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import moment from 'moment';
import { getSharedRentals } from '../../api/sharedRentalsApi';
import { simpleGroupStatus, sortSharedRentalsForAdmin } from '../../utils/adminBookingStatus';
import '../../styles/admin-manage.scss';
import './AdminManageToolbar.scss';

export default function ManageSharedRentals() {
  const location = useLocation();
  const history = useHistory();

  const [actionItems, setActionItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('needing_action') === '1') history.replace({ pathname: location.pathname, search: '' });
  }, [location.search, location.pathname, history]);

  const fetchActionItems = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const res = await getSharedRentals({ admin_list: 'true', needing_admin_action: '1', per_page: 100 });
      const raw = res.data.shared_rentals || res.data || [];
      const rentals = Array.isArray(raw) ? raw.filter((r) => r.is_deleted !== true) : [];
      setActionItems(sortSharedRentalsForAdmin(rentals));
    } catch (e) {
      console.warn('ManageSharedRentals fetch error', e);
      setLoadFailed(true);
      setActionItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActionItems();
  }, [fetchActionItems]);

  return (
    <div className="container py-4 admin-manage-page">
      <Link to="/admin" className="btn btn-outline-secondary btn-sm mb-3">← Dashboard</Link>
      <header className="admin-manage-page__header d-flex flex-wrap align-items-center justify-content-between">
        <h2 className="mb-0">Manage Shared Rentals</h2>
        <Link to="/admin/shared_rentals" className="btn btn-outline-secondary btn-sm">All shared rentals</Link>
      </header>
      <p className="admin-manage-page__subtitle">
        Rentals waiting on deposits, final payments, or vendor assignment.
      </p>

      {loadFailed && (
        <p className="text-muted mb-3">Could not load rentals right now.</p>
      )}

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="sr-only">Loading…</span></div>
        </div>
      )}

      {!loading && !loadFailed && actionItems.length === 0 && (
        <div className="alert alert-light border text-center py-5">
          <p className="mb-0 text-muted">Nothing needs your action right now.</p>
        </div>
      )}

      {!loading && !loadFailed && actionItems.length > 0 && (
        <section aria-label="Shared rentals needing action">
          <h3 className="h6 mb-3 text-muted text-uppercase">Waiting on you ({actionItems.length})</h3>
          <div className="table-responsive">
            <table className="table table-sm table-hover admin-manage-table">
              <thead>
                <tr>
                  <th>Rental</th>
                  <th>Date</th>
                  <th>Seats</th>
                  <th>Status</th>
                  <th>Next step</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {actionItems.map((rental) => {
                  const status = simpleGroupStatus({
                    ...rental,
                    group_formed: rental.rental_formed ?? rental.group_formed,
                  });
                  const paidCount = rental.paid_people_count || 0;
                  const members = rental.members || [];

                  return (
                    <tr key={rental.idx} className={status.key === 'waiting' ? 'admin-manage-table__waiting' : undefined}>
                      <td>
                        <strong>{rental.name || rental.route_name || 'Shared rental'}</strong>
                        <div className="text-muted small">{(rental.origin || '—')} → {(rental.destination || '—')}</div>
                      </td>
                      <td>{rental.start_date ? moment(rental.start_date).format('D MMM YYYY') : '—'}</td>
                      <td>{paidCount} paid · {members.length} booked</td>
                      <td><span className={`badge badge-${status.badge}`}>{status.label}</span></td>
                      <td className="text-muted small">{status.hint}</td>
                      <td className="text-right">
                        <Link to={`/admin/shared_rental_details/${rental.idx}`} className="btn btn-sm btn-primary">Manage</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
